import React from 'react';
import { Button } from '../../ui/Button/Button';
import { EmptyNote } from '../ui/EmptyNote';
import { Sheet } from '../ui/Sheet';
import { ACCENT_SPECS } from '../../../lib/rcu/catalog/accent';
import { LOAD_TYPE_SPECS } from '../../../lib/rcu/catalog/loadType';
import type { Load } from '../../../lib/rcu/model';
import { freeTargets } from '../../../lib/rcu/query/targets';
import type { ChannelTarget } from '../../../lib/rcu/query/targets';
import { useRcuConfigStore } from '../../../stores/rcuConfigStore';
import { useCommandFeedback } from '../useCommandFeedback';

export interface TargetPickerSheetProps {
  isOpen: boolean;
  /** The load being placed, or null when the sheet should stay closed. */
  load: Load | null;
  onClose: () => void;
}

/** Picks a free output for a load, showing only outputs that can drive it. */
export const TargetPickerSheet: React.FC<TargetPickerSheetProps> = ({
  isOpen,
  load,
  onClose,
}) => {
  const config = useRcuConfigStore((state) => state.config);
  const bindLoad = useRcuConfigStore((state) => state.bindLoad);
  const report = useCommandFeedback();

  if (load === null) return null;

  const options = freeTargets(config, load.type);

  const wire = (target: ChannelTarget): void => {
    if (report(bindLoad(load.id, target.ref), `${load.name} wired to ${target.shortLabel}.`)) {
      onClose();
    }
  };

  return (
    <Sheet
      isOpen={isOpen}
      title={`Wire ${load.name}`}
      subtitle={`Free outputs that drive ${LOAD_TYPE_SPECS[load.type].label.toLowerCase()}`}
      onClose={onClose}
      footer={
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
      {options.length === 0 ? (
        <EmptyNote message="Every compatible output is already in use. Add a rail or free an output first." />
      ) : (
        <ul className="grid grid-cols-2 gap-2">
          {options.map((target) => {
            const accent = ACCENT_SPECS[target.accent];
            return (
              <li key={`${target.ref.kind}-${target.shortLabel}`}>
                <button
                  type="button"
                  onClick={() => wire(target)}
                  className={`flex h-full w-full flex-col justify-between gap-2 rounded-2xl border bg-white px-4 py-3 text-left transition-colors hover:border-[#D4A054] ${accent.border}`}
                >
                  <span className={`text-xs font-medium uppercase tracking-wide ${accent.text}`}>
                    {target.shortLabel}
                  </span>
                  <span className="text-xs text-[#666666]">{target.outputHint}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </Sheet>
  );
};

export default TargetPickerSheet;
